import React, { useEffect, useRef, useState } from "react";
import {
  BACKGROUND_FRAME,
  CHEVRON_LEFT,
  EMPTY_SEARCH_2,
  PATTERN,
  PROPERTIES,
  SEARCH_ICON,
  SEARCH_INPUT_ICON,
  SEARCH_INPUT_WHITE,
  SEARCHES,
} from "../../assets";
import { useAppSelector } from "../../hooks";
import { selectUser } from "../../states/slices/authReducer";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import TableComponent from "../../shared/components/table";
import { SEARCH_HISTORY_COLUMN } from "../../shared/components/table/columns";
import { useQuery } from "react-query";
import {
  getAllSearchHistory,
  getAllTotalDetails,
  getTotalAmountSpent,
} from "../../Services/services";
import { formatCurrency } from "../../utils";
import { SearchHistoryTypes } from "../../types";
import PropertyModal from "../../shared/components/propertyModal";
import { Loader2 } from "lucide-react";
import LinkHead from "../../shared/components/LinkHead";

function Dashboard() {
  const user = useAppSelector(selectUser);
  const [searchQuery, setSearchQuery] = useState("");
  const [propertyModal, setPropertyModal] = useState(false);
  const { data: searchHistory, isLoading } = useQuery(
    "search",
    getAllSearchHistory,
    {
      refetchOnWindowFocus: true,
    },
  );
  const { data: dashboardData } = useQuery("total", getAllTotalDetails);
  const { data: amountSpent } = useQuery("amount-spent", getTotalAmountSpent);
  const [showAmount, setShowAmount] = useState(false);
  const [currentpage, setCurrentpage] = useState(0);
  const filterProperty = searchHistory?.data?.filter(
    (item: SearchHistoryTypes) =>
      item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.address?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  console.log(searchHistory);

  const pagesRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const selectedButton = pagesRefs.current[currentpage];
    if (selectedButton) {
      selectedButton.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [currentpage]);
  return (
    <div className="p-5">
      <LinkHead title="individual - dashboard" content="" />
      <div className="flex items-start flex-col">
        <span className="text-[#2A2E34] font-semibold text-2xl">Dashboard</span>
        <span className="text-primary-50 font-medium text-sm">
          Dashboard Overview
        </span>
      </div>
      {/* Changed from horizontal to vertical layout on mobile */}
      <div className="w-full flex flex-col lg:flex-row items-start gap-4 mt-5">
        <div
          className="w-full lg:w-2/3 bg-globalGold rounded-3xl p-5"
          style={{
            backgroundImage: `url(${BACKGROUND_FRAME})`,
            backgroundSize: "cover",
            backgroundPosition: "contain",
          }}
        >
          <div className="flex flex-col gap-y-4">
            <div className="flex flex-col gap-y-2">
              <span className="text-white font-bold text-xl">
                Hello, {user?.name?.split(" ")[0]}
              </span>
              <span className="text-sm font-medium text-white">
                The User Dashboard is designed to efficiently manage the biodata
                of your searches.
              </span>
            </div>
            {/* Made the stats and button responsive */}
            <div className="w-full flex flex-col lg:flex-row items-center justify-between">
              <div className="flex flex-col lg:flex-row items-center gap-4 lg:gap-x-8 mb-4 lg:mb-0">
                {/* <div className="flex items-start gap-x-2">
                  <img src={PROPERTIES} alt="" />
                  <div className="flex flex-col items-start">
                    <span className="font-medium text-white text-xs">
                      Total Properties
                    </span>
                    <span className="text-xl font-bold text-white">
                      {dashboardData?.total_case}
                    </span>
                  </div>
                </div> */}
                <div className="flex items-start gap-x-2">
                  <img src={SEARCHES} alt="" />
                  <div className="flex flex-col items-start">
                    <span className="font-medium text-white text-xs">
                      Total Searches
                    </span>
                    <span className="text-xl font-bold text-white">
                      {" "}
                      {dashboardData?.total_search_property}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setPropertyModal(true)}
                className="w-fit hover:scale-95 transition-all duration-300 bg-white px-5 py-2 rounded-3xl flex items-center justify-center gap-x-2"
              >
                <img src={SEARCH_ICON} className="w-[20px] h-[20px]" alt="" />
                <span className="text-[16px] font-medium text-globalGold">
                  Search Property
                </span>
              </button>

              <PropertyModal
                open={propertyModal}
                close={() => setPropertyModal(false)}
              />
            </div>
          </div>
        </div>
        {/* Changed fixed width to responsive width */}
        <div
          style={{
            backgroundImage: `url(${PATTERN})`,
            backgroundSize: "cover",
            backgroundPosition: "contain",
          }}
          className="w-full lg:w-1/3 py-11 bg-black flex-col px-5 text-white h-full rounded-3xl flex items-center justify-center"
        >
          <span className="font-normal text-sm">Total Amount Spent</span>
          <div className="flex items-center gap-x-2">
            <span className="text-[32px] font-bold">
              {showAmount
                ? formatCurrency({
                    iso: "en-ng",
                    slug: "NGN",
                  }).format(amountSpent?.total)
                : "...."}
            </span>

            <span
              className="cursor-pointer"
              onClick={() => setShowAmount((prev) => !prev)}
            >
              {!showAmount ? <FaEye /> : <FaEyeSlash />}
            </span>
          </div>
        </div>
      </div>
      <div className="w-full mt-8 border border-borderColor py-4 rounded-xl">
        {/* Made search section responsive */}
        <div className="w-full flex flex-col lg:flex-row justify-between items-center px-4 gap-3">
          <span className="text-[#2A2E34] font-semibold text-[16px]">
            Search History
          </span>
          <div className="w-full lg:w-fit items-center gap-x-2 flex px-3 py-2 rounded-xl border border-borderColor">
            <img src={SEARCH_INPUT_ICON} className="w-[20px] h-[20px]" alt="" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent placeholder:text-sm outline-none"
              placeholder="Search property title,location"
            />
          </div>
        </div>

        {searchHistory?.data?.length > 0 ? (
          <div className="mt-2 overflow-x-auto">
            {" "}
            {/* Added overflow for table responsiveness */}
            <TableComponent
              DATA={filterProperty}
              //@ts-ignore
              COLUMNS={SEARCH_HISTORY_COLUMN}
            />
            <div className="flex items-end justify-end pt-3 space-x-5 pr-5">
              <button
                type="button"
                onClick={() => setCurrentpage(searchHistory.current_page - 1)}
                disabled={searchHistory.current_page === 1}
                className={`border border-[#EBEBEB] rounded-xl w-[40px] h-[40px] lg:flex hidden items-center gap-x-2 justify-center ${
                  searchHistory.current_page === 1 && ""
                }`}
              >
                <img src={CHEVRON_LEFT} className="w-[20px] h-[20px] " alt="" />
              </button>
              <div
                className={`flex items-center gap-x-2 ${
                  searchHistory?.meta.links?.slice(1, -1).length > 5 &&
                  "max-w-[200px] overflow-x-auto"
                }`}
              >
                {searchHistory?.meta?.links
                  .slice(1, -1)
                  .map((page: any, index: number) => (
                    <button
                      ref={(el) => (pagesRefs.current[index] = el)}
                      onClick={() => {
                        page.url && setCurrentpage(Number(page.label));
                      }}
                      className={`flex items-center justify-center pt-[0.5px] hover:bg-globalGold hover:text-white transition-all duration-300 gap-x-3 min-w-[40px] h-[40px] rounded-xl ${
                        page.active
                          ? "bg-globalGold text-white"
                          : "border text-[#2A2E34]"
                      } `}
                    >
                      <span className={`text-[13px]`}>{page.label}</span>
                    </button>
                  ))}
              </div>
              <button
                type="button"
                onClick={() =>
                  setCurrentpage(searchHistory.meta.current_page + 1)
                }
                disabled={
                  searchHistory.meta.current_page ===
                  searchHistory.meta.last_page
                }
                className={`border border-[#EBEBEB] rounded-xl gap-x-2 w-[40px] h-[40px] lg:flex hidden items-center justify-center ${
                  searchHistory.meta.current_page ===
                    searchHistory.meta.last_page && ""
                }`}
              >
                <img
                  src={CHEVRON_LEFT}
                  className="w-[20px] h-[20px] scale-x-[-1]"
                  alt=""
                />
              </button>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center">
            <Loader2 color="#9ca3af" size={30} className="animate-spin" />
          </div>
        ) : (
          <div className="flex flex-col gap-y-2 pb-5 justify-center items-center w-full">
            <img
              src={EMPTY_SEARCH_2}
              className="w-[147.69px] h-[120px]"
              alt=""
            />
            <span className="text-xl font-bold text-customDark">
              No Searches Yet
            </span>
            <span className="text-sm font-medium text-primary-50 w-full px-4 lg:w-96 text-center">
              No search history to be displayed, click the button below to begin
              your search.
            </span>
            <button
              onClick={() => setPropertyModal(true)}
              className="w-fit hover:scale-95 transition-all duration-300 bg-globalGold px-5 py-2 rounded-3xl flex items-center justify-center gap-x-2"
            >
              <img
                src={SEARCH_INPUT_WHITE}
                className="w-[20px] h-[20px]"
                alt=""
              />
              <span className="text-[16px] font-medium text-white">
                Search Property
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
